import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableSortLabel,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Checkbox,
} from '@mui/material';
import { Edit, Add } from '@mui/icons-material';
import { stableSort, compareByNumericProp } from '../../utils/comparators';
import { beginEditingUser, beginNewUser } from '../../redux/actions/users';

import './users-table.css';

const UsersTable = ({ userData, beginEditing, newUser, editingId, editType, scrollTop }) => {
  const [{ order, orderBy }, setSort] = useState({ orderBy: 'org_name', order: 'asc' });
  const [hideInactive, setHideInactive] = useState(true);

  const filteredUsers = useMemo(
    () => (hideInactive ? userData.filter((user) => user.active) : userData),
    [userData, hideInactive]
  );
  const comparator = compareByNumericProp(orderBy, order);
  const createSortHandler = (sortColumn) => () => {
    // first click on a column sort label sorts it by 'ascending' regardless of last order
    const isAsc = order === 'asc' && orderBy === sortColumn;
    setSort({ orderBy: sortColumn, order: isAsc ? 'desc' : 'asc' });
  };
  const sortLabel = (key, text) => {
    const isSort = orderBy === key;
    return (
      <TableSortLabel
        active={isSort}
        direction={isSort ? order : 'asc'}
        onClick={createSortHandler(key)}
      >
        {text}
      </TableSortLabel>
    );
  };
  return (
    <div className="user-table">
      <Stack direction="row" spacing={8} sx={{ display: 'block', textAlign: 'right' }}>
        <>
          <Checkbox
            size="small"
            checked={hideInactive}
            onChange={(event, newVal) => setHideInactive(newVal)}
          />
          Hide Inactive
        </>
        <Button variant="contained" color="primary" onClick={() => newUser()}>
          <Add />
          Create new User
        </Button>
      </Stack>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Edit</TableCell>
            <TableCell>{sortLabel('email', 'Email')}</TableCell>
            <TableCell>{sortLabel('last_name', 'Name')}</TableCell>
            <TableCell>{sortLabel('org_name', 'Organization')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {stableSort(filteredUsers, comparator).map((user) => (
            <TableRow
              className={`user-row ${!user.active ? 'inactive' : ''}`}
              key={user.id}
              hover
              selected={editType === 'USER' && editingId === user.id}
              classes={{ selected: 'selected' }}
            >
              <TableCell>
                <IconButton
                  onClick={() => {
                    scrollTop();
                    beginEditing(user.id);
                  }}
                  size="large"
                >
                  <Edit />
                </IconButton>
              </TableCell>
              <TableCell>
                <span className="email">{user.email}</span>
              </TableCell>
              <TableCell>
                {user.first_name} {user.last_name}
              </TableCell>
              <TableCell>{user.org_name}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default connect(
  ({
    user: { orgId },
    users: {
      users,
      editing: { editType, id },
    },
  }) => ({
    orgId,
    userData: (users || []).filter((u) => u.org_id === orgId),
    editType,
    editingId: id,
  }),
  (dispatch) => ({
    beginEditing: (id) => dispatch(beginEditingUser(id)),
    newUser: () => dispatch(beginNewUser()),
  })
)(UsersTable);
